import React from "react";

import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { css, styled } from "styled-components";

import {
  gridSpacing,
  colorTheme,
  makeHumanReadable,
  internalPageError,
} from "@utils";

import { default as LHButton } from "../Button/Button";
import { default as LHSingleDatePicker } from "../DatePicker/SingleDatePicker";
import { default as LHSelect } from "../Select/Select";
import { default as LHStatusPill } from "../StatusPill/StatusPill";
import { default as LHSwitch } from "../Switch/Switch";
import TableLink from "../Table/TableLink";
import type { ButtonStatus } from "../TableExport/TableExport";
import Text from "../Text/Text";
import { default as LHTextArea } from "../TextArea/TextArea";
import { default as LHTimePicker } from "../TimePicker/TimePicker";
import ToolTip from "../ToolTip/ToolTip";

dayjs.extend(customParseFormat);

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  scroll-margin-top: ${gridSpacing[6]}px;
`;

const LargeHeader = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: ${colorTheme("neutral")};
  font-family: "Manrope";
`;

const Header = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: ${colorTheme("neutralL1")};
`;

const SubHeaderStyle = styled.p<{
  $mb?: boolean;
  $primary?: boolean;
  $url?: boolean;
}>`
  font-size: 14px;
  font-weight: 400;
  color: ${({ $primary }) =>
    $primary ? colorTheme("primary") : colorTheme("neutral")};
  margin-bottom: ${({ $mb }) => ($mb ? gridSpacing[2] : 0)}px;
  ${({ $url }) =>
    $url &&
    css`
      display: inline-block;
    `};
`;

interface SubHeaderProps {
  mb?: boolean;
  primary?: boolean;
  url?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

const SubHeader = ({ mb, primary, url, children, style }: SubHeaderProps) => {
  if (url) {
    return (
      <SubHeaderStyle $url={!!url} $mb={mb} $primary={primary} style={style}>
        <TableLink newTab url={url}>
          {children}
        </TableLink>
      </SubHeaderStyle>
    );
  }
  return (
    <SubHeaderStyle $url={!!url} $mb={mb} $primary={primary} style={style}>
      {children}
    </SubHeaderStyle>
  );
};

const Body = styled.p<{
  $light?: boolean;
  $lightest?: boolean;
  $small?: boolean;
}>`
  font-size: ${({ $small }) => ($small ? 11 : 12)}px;
  font-weight: 400;
  color: ${({ $light, $lightest }) =>
    $lightest
      ? colorTheme("neutralL2")
      : $light
        ? colorTheme("neutralL1")
        : colorTheme("neutral")};
`;

const Row = styled.div<{ $wrap?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: ${({ $wrap }) => ($wrap ? "wrap" : "nowrap")};
  height: 100%;
  gap: ${gridSpacing[4]}px;
  > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gridSpacing[4]}px;
`;

const RankContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${gridSpacing[2]}px;
`;

const RankCircle = styled.div<{ $hide?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  width: 20px;
  min-width: 20px;
  height: 20px;
  min-height: 20px;
  border-radius: 50%;
  background: ${colorTheme("neutralL4")};
  margin-right: ${gridSpacing[3]}px;
  margin-top: 3px;
  ${({ $hide }) =>
    $hide
      ? css`
          opacity: 0;
          * {
            cursor: default !important;
            pointer-events: none;
          }
        `
      : ""}
`;

interface ViewTextProps {
  title: string;
  text?: React.ReactNode;
  cleanText?: boolean;
  testId?: string;
  url?: string;
  textStyle?: React.CSSProperties;
}

const ViewText = ({
  title,
  text,
  testId,
  cleanText,
  url,
  textStyle,
}: ViewTextProps) => {
  let content = text || "-";

  if (!React.isValidElement(content)) {
    if (cleanText && content !== "-" && typeof content === "string") {
      content = makeHumanReadable(content);
    }
    if (dayjs(content as string, "YYYY-MM-DD", true).isValid()) {
      content = dayjs(content as string).format("MM/DD/YYYY");
    }
  }

  const titleElement = (
    <p
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: colorTheme("neutralL2"),
      }}
    >
      {title}
    </p>
  );

  const textElement = (
    <p
      style={{
        fontSize: 12,
        color: url ? colorTheme("primary") : colorTheme("neutral"),
        fontWeight: 500,
        wordWrap: "break-word",
        overflowWrap: "break-word",
        minWidth: 0,
        ...textStyle,
      }}
    >
      {content}
    </p>
  );

  if (url) {
    return (
      <div
        data-testid={testId}
        style={{
          width: "100%",
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {titleElement}
        <TableLink newTab url={url}>
          {textElement}
        </TableLink>
      </div>
    );
  }

  return (
    <div data-testid={testId} style={{ width: "100%", overflow: "hidden" }}>
      {titleElement}
      {textElement}
    </div>
  );
};

interface EditTextProps {
  required?: boolean;
  placeholder?: string;
  label: string;
  value?: string | number;
  error?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  testId?: string;
  disabled?: boolean;
  type?: string;
  step?: number;
  min?: number;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  style?: React.CSSProperties;
}

const EditText = ({
  style,
  required,
  placeholder,
  label,
  value,
  error,
  onChange,
  testId,
  disabled,
  type = "text",
  step,
  onBlur,
  min,
}: EditTextProps) => (
  <Text
    onBlur={onBlur}
    placeholder={placeholder}
    type={type}
    disabled={disabled}
    required={required}
    requiredStyle={{
      fontSize: 12,
    }}
    helperStyle={{
      fontSize: 12,
      position: "absolute",
      bottom: -18,
    }}
    labelStyle={{
      color: colorTheme("neutralL2"),
      fontSize: 12,
      fontWeight: 500,
    }}
    inputStyle={{
      fontSize: 12,
      color: colorTheme("neutral"),
      fontWeight: 500,
      height: 22,
    }}
    style={{
      marginBottom: 0,
      minWidth: 0,
      position: "relative",
      ...style,
    }}
    label={label}
    value={value?.toString() ?? ""}
    error={error}
    onChange={onChange}
    testId={testId}
    step={step}
    min={min ? min : type === "number" ? 0 : undefined}
  />
);

interface TextAreaProps {
  disabled?: boolean;
  error?: string;
  label: string;
  onBlur?: () => void;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  style?: React.CSSProperties;
  testId?: string;
  type?: string;
  value?: string | number;
}

const TextArea = ({
  disabled,
  error,
  label,
  onBlur,
  onChange,
  placeholder,
  required,
  style,
  testId,
  value,
}: TextAreaProps) => (
  <LHTextArea
    rows={4}
    onBlur={onBlur}
    placeholder={placeholder}
    disabled={disabled}
    required={required}
    requiredStyle={{
      fontSize: 12,
    }}
    helperStyle={{
      fontSize: 12,
      position: "absolute",
      bottom: -18,
    }}
    labelStyle={{
      color: colorTheme("neutralL2"),
      fontSize: 12,
      fontWeight: 500,
    }}
    textareaStyle={{
      fontSize: 12,
      color: colorTheme("neutral"),
      fontWeight: 500,
    }}
    style={{
      marginBottom: 0,
      minWidth: 0,
      position: "relative",
      ...style,
    }}
    label={label}
    value={value?.toString() ?? ""}
    error={error}
    onChange={onChange}
    testId={testId}
  />
);

type ValueProps = string | string[] | null;

interface SelectProps {
  isMulti?: boolean;
  required?: boolean;
  label: string;
  placeholder?: string;
  emptyOption?: boolean;
  options: { label: string; value: string }[];
  error?: string;
  value?: ValueProps;
  onChange: (value: ValueProps, parentPath?: string | undefined) => void;
  isCreatable?: boolean;
  testId?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const Select = ({
  disabled,
  isMulti,
  required,
  label,
  placeholder,
  emptyOption,
  options,
  error,
  value,
  onChange,
  isCreatable,
  testId,
  style,
}: SelectProps) => (
  <LHSelect
    disabled={disabled}
    style={{ position: "relative", ...style }}
    isCreatable={isCreatable}
    isMulti={isMulti}
    helperTextStyle={{
      fontSize: 12,
      bottom: -18,
      position: "absolute",
      width: "100%",
    }}
    optionStyle={{ fontSize: 12 }}
    controlStyle={{ minHeight: 28, boxSizing: "border-box" }}
    valueContainerStyle={{ minHeight: 28, padding: "0 8px", fontSize: 12 }}
    indicatorsContainerStyle={{ height: 28 }}
    indicatorSeparatorStyle={{ display: "none" }}
    inputStyle={{ margin: 0, fontSize: 12 }}
    placeholderStyle={{
      fontSize: 12,
    }}
    singleValueStyle={{
      fontSize: 12,
      color: colorTheme("neutral"),
      fontWeight: 500,
    }}
    labelStyle={{
      color: colorTheme("neutralL2"),
      fontSize: 12,
      fontWeight: 500,
    }}
    requiredStyle={{
      fontSize: 12,
    }}
    required={required}
    label={label}
    placeholder={placeholder}
    emptyOption={emptyOption}
    options={options}
    error={error}
    value={value}
    onChange={onChange}
    testId={testId}
  />
);

interface SingleDatePickerProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  testId?: string;
  error?: string;
  required?: boolean;
  style?: React.CSSProperties;
}

const SingleDatePicker = ({
  label,
  value,
  onChange,
  testId,
  error,
  required,
  style,
}: SingleDatePickerProps) => (
  <LHSingleDatePicker
    fixedPosition
    required={required}
    error={error}
    label={label}
    value={value}
    onChange={onChange}
    testId={testId}
    style={{ marginBottom: 0, ...style }}
    labelStyle={{
      color: colorTheme("neutralL2"),
      fontSize: 12,
      fontWeight: 500,
    }}
    requiredStyle={{ fontSize: 12 }}
    inputStyle={{
      width: "100%",
      fontSize: 12,
      height: 30,
      boxSizing: "border-box",
      color: colorTheme("neutral"),
      fontWeight: 500,
      fontFamily: "inherit",
    }}
    helperTextStyle={{
      fontSize: 12,
      position: "absolute",
      width: "initial",
    }}
  />
);

const Spacer = styled.div<{ $dashed?: boolean; $even?: boolean }>`
  width: 100%;
  height: 0;
  border-bottom: 1px solid ${colorTheme("neutralL4")};
  border-bottom-style: ${({ $dashed }) => ($dashed ? "dashed" : "solid")};
  ${({ $even }) =>
    $even
      ? css`
          margin-top: ${gridSpacing[3]}px;
          margin-bottom: ${gridSpacing[3]}px;
        `
      : css`
          margin-top: ${gridSpacing[2]}px;
          margin-bottom: ${gridSpacing[4]}px;
        `}
`;

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  disabled?: boolean;
}
const TimePicker = ({ value, onChange, label, disabled }: TimePickerProps) => (
  <LHTimePicker
    use24Hr
    value={value}
    onChange={onChange}
    label={label}
    disabled={disabled}
    minOptions={["00", "15", "30", "45"].map((obj) => {
      return {
        label: obj,
        value: obj,
      };
    })}
    hrStyle={{ margin: 0, width: "100%" }}
    aaStyle={{ margin: 0, width: "100%" }}
    minStyle={{ margin: 0, width: "100%" }}
    containerStyle={{ gap: gridSpacing[1] }}
    helperTextStyle={{ fontSize: 12 }}
    optionStyle={{ fontSize: 12 }}
    controlStyle={{
      minHeight: 28,
      boxSizing: "border-box",
      flexWrap: "nowrap",
    }}
    valueContainerStyle={{
      minHeight: 28,
      padding: "0 8px",
      fontSize: 12,
    }}
    indicatorsContainerStyle={{ height: 28 }}
    indicatorSeparatorStyle={{ display: "none" }}
    inputStyle={{ margin: 0, fontSize: 12 }}
    placeholderStyle={{
      fontSize: 12,
    }}
    singleValueStyle={{
      fontSize: 12,
      color: colorTheme("neutral"),
      fontWeight: 500,
    }}
    labelStyle={{
      color: colorTheme("neutralL2"),
      fontSize: 12,
      fontWeight: 500,
    }}
    requiredStyle={{
      fontSize: 12,
    }}
  />
);

interface ButtonProps {
  text: string;
  variant?: "primary" | "secondary";
  onClick: () => void;
  status?: ButtonStatus;
  testId?: string;
  affirmationText?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  icon?: string;
}

const Button = ({
  text,
  variant,
  onClick,
  status,
  testId,
  affirmationText,
  disabled,
  style,
  icon,
}: ButtonProps) => (
  <LHButton
    style={{ height: 28, ...style }}
    icon={icon}
    text={text}
    variant={variant}
    onClick={onClick}
    disabled={disabled}
    status={status}
    testId={testId}
    affirmationText={affirmationText}
  />
);

interface AddButtonProps {
  onClick: () => void;
  status?: ButtonStatus;
  testId?: string;
  affirmationText?: string;
  disabled?: boolean;
  text?: string;
}

const AddButton = ({
  onClick,
  status,
  testId,
  affirmationText,
  disabled,
  text,
}: AddButtonProps) => (
  <LHButton
    style={{
      height: 28,
      width: 28,
      fontSize: 18,
      padding: "0 0",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      borderColor: colorTheme("primary"),
    }}
    icon="fa-regular fa-plus"
    iconStyle={{ color: colorTheme("primary") }}
    variant="secondary"
    onClick={onClick}
    disabled={disabled}
    status={status}
    testId={testId}
    affirmationText={affirmationText}
    text={text}
  />
);

const iconButtonClasses = {
  edit: "fa-pen fa-regular",
  save: "fa-check fa-regular",
  cancel: "fa-times fa-regular",
  up: "fa-arrow-up fa-regular",
  down: "fa-arrow-down fa-regular",
  delete: "fa-trash fa-regular",
  versionHistory: "fa-clock-rotate-left fa-regular",
  revert: "fa-rotate-reverse fa-regular",
  clone: "fa-copy fa-regular",
  run: "fa-play fa-solid",
  view: "fa-eye fa-regular",
  lineage: "fa-regular fa-diagram-project",
} as const;

const IconButtonStyle = styled.i<{
  $hoverColor?: string;
  $activeColor?: string;
  $color?: string;
  $disabled?: boolean;
}>`
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  color: ${({ $color }) => $color};

  ${({ $hoverColor, $disabled }) =>
    $hoverColor &&
    !$disabled &&
    css`
      &:hover {
        color: ${$hoverColor};
      }
    `}

  ${({ $activeColor, $disabled }) =>
    $activeColor &&
    !$disabled &&
    css`
      &:active {
        color: ${$activeColor};
      }
    `}
`;

interface IconButtonProps {
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  testId?: string;
  type?: keyof typeof iconButtonClasses;
  color?: string;
  hoverColor?: string;
  activeColor?: string;
  tooltip?: string;
  disabled?: boolean;
}

const IconButton = ({
  style,
  onClick,
  testId,
  type = "edit",
  color = colorTheme("neutral"),
  hoverColor = colorTheme("neutralL2"),
  activeColor = colorTheme("neutralL1"),
  tooltip,
  disabled,
}: IconButtonProps) => {
  let _color = color;
  if (disabled) {
    _color = colorTheme("neutralL3");
  } else if (type === "cancel") {
    _color = colorTheme("danger");
  }

  const getButton = () => (
    <IconButtonStyle
      $color={_color}
      $hoverColor={hoverColor}
      $activeColor={activeColor}
      $disabled={disabled}
      className={`${iconButtonClasses[type]}`}
      style={{
        fontSize: type === "save" || type === "cancel" ? 18 : 16,
        ...style,
      }}
      data-testid={testId}
      onClick={onClick}
    />
  );

  if (tooltip) {
    return (
      <ToolTip
        text={tooltip}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {getButton()}
      </ToolTip>
    );
  }

  return getButton();
};

const SpacerCircle = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${colorTheme("neutralL4")};
`;

interface RankControlsProps {
  baseURL: string;
  UUID: string;
  successCallback: () => void;
}

const RankControls = ({
  baseURL,
  UUID,
  successCallback,
}: RankControlsProps) => (
  <>
    <IconButton
      type="up"
      onClick={() =>
        axios
          .post(`${baseURL}/${UUID}/rank_up`)
          .then(() => successCallback())
          .catch((err) => {
            console.error(err);
            internalPageError("Something went wrong updating rank");
          })
      }
    />
    <IconButton
      type="down"
      onClick={() =>
        axios
          .post(`${baseURL}/${UUID}/rank_down`)
          .then(() => successCallback())
          .catch((err) => {
            console.error(err);
            internalPageError("Something went wrong updating rank");
          })
      }
    />
  </>
);

interface StatusPillProps {
  text: "ACTIVE" | "INACTIVE" | "SYSTEM_MANAGED";
}
const StatusPill = ({ text }: StatusPillProps) => {
  const getStatusPillProps = (value: typeof text) => {
    const statusMap = {
      ACTIVE: {
        type: "success",
        text: "Active",
        style: {
          boxShadow: "none",
        },
      },
      INACTIVE: {
        type: "danger",
        text: "Inactive",
        style: {
          boxShadow: "none",
        },
      },
      SYSTEM_MANAGED: {
        type: "neutral",
        text: "System Managed",
        style: {
          boxShadow: "none",
          color: colorTheme("neutralL1"),
          backgroundColor: colorTheme("neutralL4"),
        },
      },
    } as const;

    return statusMap[value] ?? ({ text: "N/A", type: "default" } as const);
  };

  return <LHStatusPill {...getStatusPillProps(text)} />;
};

const ButtonRow = styled.div`
  display: flex;
  gap: ${gridSpacing[4]}px;
  align-items: center;
`;

const ButtonContainer = styled.div<{ $hide?: boolean }>`
  display: flex;
  justify-content: flex-end;
  gap: ${gridSpacing[3]}px;
  width: 100%;
  flex-grow: 0;
  ${({ $hide }) =>
    $hide
      ? css`
          opacity: 0;
          * {
            cursor: default !important;
            pointer-events: none;
          }
        `
      : ""}
`;

interface SwitchProps {
  label: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const Switch = ({ label, checked, onChange }: SwitchProps) => (
  <div
    style={{
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
    }}
  >
    <LHSwitch
      label={label}
      checked={checked}
      onChange={onChange}
      labelDirection="left"
      style={{
        display: "flex",
        alignItems: "center",
        gap: gridSpacing[2],
      }}
      labelStyle={{
        color: colorTheme("neutralL2"),
        fontSize: 12,
        fontWeight: 500,
      }}
    />
  </div>
);

type StatusProp = "failed" | "in_progress";

const FloatingPanelStyle = styled.div<{
  $hover?: boolean;
  $status?: StatusProp;
  $topBorderColor?: string;
  $disabled?: boolean;
}>`
  position: relative;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.07);
  margin: 0 ${gridSpacing[7]}px;
  margin-bottom: ${gridSpacing[5]}px;
  transition: all 0.2s ease-in-out;
  border-radius: 6px;
  ${({ $status }) =>
    !$status &&
    css`
      border: 1px solid ${colorTheme("neutralL4")};
    `}
  padding: ${gridSpacing[5]}px;
  ${({ $hover, $disabled }) =>
    $hover &&
    css`
      &:hover {
        background-color: ${colorTheme("infoL3")};
        cursor: ${$disabled ? "not-allowed" : "pointer"};
        box-shadow: none;
      }
    `}
  ${({ $status }) =>
    $status === "failed"
      ? css`
          border-left: 8px solid ${colorTheme("danger")};
        `
      : $status === "in_progress"
        ? css`
            border-left: 8px solid ${colorTheme("info")};
          `
        : ""}
    ${({ $topBorderColor }) =>
    $topBorderColor &&
    css`
      border-top: 3px solid ${$topBorderColor};
    `}
`;

const FloatingPanel: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
  hover?: boolean;
  onClick?: () => void;
  status?: StatusProp;
  topBorderColor?: string;
  disabled?: boolean;
}> & {
  AddButton: typeof AddButton;
  Body: typeof Body;
  Button: typeof Button;
  ButtonContainer: typeof ButtonContainer;
  ButtonRow: typeof ButtonRow;
  Column: typeof Column;
  EditText: typeof EditText;
  Header: typeof Header;
  HeaderRow: typeof HeaderRow;
  IconButton: typeof IconButton;
  LargeHeader: typeof LargeHeader;
  RankCircle: typeof RankCircle;
  RankContainer: typeof RankContainer;
  RankControls: typeof RankControls;
  Row: typeof Row;
  Select: typeof Select;
  SingleDatePicker: typeof SingleDatePicker;
  Spacer: typeof Spacer;
  SpacerCircle: typeof SpacerCircle;
  StatusPill: typeof StatusPill;
  SubHeader: typeof SubHeader;
  Switch: typeof Switch;
  TextArea: typeof TextArea;
  TimePicker: typeof TimePicker;
  ViewText: typeof ViewText;
} = ({ children, style, hover, onClick, status, topBorderColor, disabled }) => (
  <FloatingPanelStyle
    $status={status}
    $hover={hover}
    $topBorderColor={topBorderColor}
    $disabled={disabled}
    style={style}
    onClick={onClick}
  >
    {children}
  </FloatingPanelStyle>
);

FloatingPanel.AddButton = AddButton;
FloatingPanel.Body = Body;
FloatingPanel.Button = Button;
FloatingPanel.ButtonContainer = ButtonContainer;
FloatingPanel.ButtonRow = ButtonRow;
FloatingPanel.Column = Column;
FloatingPanel.EditText = EditText;
FloatingPanel.Header = Header;
FloatingPanel.HeaderRow = HeaderRow;
FloatingPanel.IconButton = IconButton;
FloatingPanel.LargeHeader = LargeHeader;
FloatingPanel.RankCircle = RankCircle;
FloatingPanel.RankContainer = RankContainer;
FloatingPanel.RankControls = RankControls;
FloatingPanel.Row = Row;
FloatingPanel.Select = Select;
FloatingPanel.SingleDatePicker = SingleDatePicker;
FloatingPanel.Spacer = Spacer;
FloatingPanel.SpacerCircle = SpacerCircle;
FloatingPanel.StatusPill = StatusPill;
FloatingPanel.SubHeader = SubHeader;
FloatingPanel.Switch = Switch;
FloatingPanel.TextArea = TextArea;
FloatingPanel.TimePicker = TimePicker;
FloatingPanel.ViewText = ViewText;

export default FloatingPanel;
